import React from 'react';
import { Clapperboard, Lightbulb, Workflow } from 'lucide-react';

export type WorkspaceView = 'ideas' | 'nodes' | 'edit';

interface WorkspaceViewSwitchProps {
  view: WorkspaceView;
  onChange: (view: WorkspaceView) => void;
  /** 'light' floats over the canvas, 'dark' sits in the Edit page's bottom bar. */
  tone?: 'light' | 'dark';
}

const VIEWS: { id: WorkspaceView; label: string; hint: string; Icon: typeof Workflow }[] = [
  { id: 'ideas', label: 'Ideas', hint: 'A board of ideas for this workspace: write, think up, keep and give them out', Icon: Lightbulb },
  { id: 'nodes', label: 'Nodes', hint: 'The node canvas', Icon: Workflow },
  { id: 'edit', label: 'Video edit', hint: 'The timeline, with every video of this workspace', Icon: Clapperboard },
];

/**
 * The page that is open: lavender, as everything that is on in the canvas's bars (the bar is
 * glass, so the white pill of a segmented control on its cream track would not show on it).
 */
const OPEN_ON_LIGHT = 'aria-selected:bg-lavender-soft aria-selected:text-lavender-deep aria-selected:shadow-none';
/** On the dark Edit page: cream words, lighter under the pointer, the full lavender for the page that is open. */
const ON_DARK = 'text-cream/70 hover:text-cream hover:bg-white/10 aria-selected:bg-lavender aria-selected:text-ink aria-selected:shadow-none';

/** The page switch at the bottom of a workspace: the Ideas page, the node canvas or the Edit page. */
export const WorkspaceViewSwitch: React.FC<WorkspaceViewSwitchProps> = ({ view, onChange, tone = 'light' }) => {
  const dark = tone === 'dark';
  return (
    // One of the canvas's bars: a glass pill, 4px around 32px controls (42px in all), the same
    // size on both pages so it does not move when the page changes
    <div
      role="tablist"
      aria-label="Workspace page"
      className={`ns-bar flex p-1 ${dark ? 'bg-white/5 border border-white/10' : 'ns-glass ns-glass-strong'}`}
      data-workspace-view-switch
    >
      {/* The segmented control without a track of its own: the bar is its track */}
      <div role="presentation" className="ns-seg p-0 bg-transparent">
      {VIEWS.map(({ id, label, hint, Icon }) => {
        const open = view === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={open}
            title={hint}
            onClick={() => onChange(id)}
            className={`ns-seg-item gap-2 h-8 px-4 ${dark ? ON_DARK : OPEN_ON_LIGHT}`}
            data-view={id}
          >
            <Icon size={14} aria-hidden="true" />
            {label}
          </button>
        );
      })}
      </div>
    </div>
  );
};

export default WorkspaceViewSwitch;
